/**
 * تنخواه — صندوقی که شارژ می‌شود و از آن هزینه پرداخت می‌شود.
 *
 * چرا جدا از حسابِ اشخاص: تنخواه **موجودی** دارد، نه بدهکاری/بستانکاری. اگر
 * داخلِ حسابِ پرسنل می‌نشست، ماندهٔ آدم‌ها با موجودیِ صندوق قاطی می‌شد.
 *
 * v2.102.0 (خواستهٔ کاربر): ویرایشِ نفر‌به‌نفرِ حرکت‌ها (قبلی/بعدی)، ویرایشِ صندوق
 * (ماندهٔ اول دوره…)، «تطبیق موجودی» (شمارشِ واقعی → سندِ مغایرت) و «صفر کردن».
 * حرکت‌هایی که از دفتر مالی آمده‌اند (پرداخت/دریافت با مبدأِ تنخواه) اینجا فقط
 * دیده می‌شوند و از همان سند ویرایش می‌شوند.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Col, Dropdown, Form, Input, Modal, Popconfirm, Row, Segmented, Select,
  Space, Statistic, Table, Tag, Tooltip, Typography, message,
} from "antd";
import {
  DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SettingOutlined,
  SwapOutlined, UserSwitchOutlined,
} from "@ant-design/icons";
import Alert from "../../components/GxAlert";
import GxTable from "../../components/GxTable";
import RecordEditorModal from "../../components/RecordEditorModal";
import {
  addPettyEntry, createPettyAccount, deletePettyEntry, getPettyAccounts,
  getPettyEntries, postPettyEntryToPerson, reconcilePettyAccount, updatePettyAccount, updatePettyEntry,
} from "../../api/client";
import { loadPettyAccounts } from "../../components/SourceSelect";
import { apiError } from "../../utils/apiError";
import { MoneyInput, formatMoney } from "../../utils/money";
import { JalaliDateInput, todayISO, toJalali } from "../../utils/dateUtils";
import { filterByChildren } from "../../utils/selectFilter";

const { Text } = Typography;
const fa = (v) => formatMoney(v);
const KIND_COLOR = { manual: "default", linked: "geekblue", adjustment: "purple" };
// v2.154.0 — پرداخت/دریافتِ نقدی از صندوق که باید در حسابِ خودِ آن شخص هم بنشیند
const PERSON_TYPES = {
  out: [{ value: "advance", label: "مساعده (بدهکارِ شخص)" }, { value: "salary_paid", label: "پرداخت حقوق" }],
  in: [{ value: "cash_repayment", label: "بازپرداخت (بستانکارِ شخص)" }, { value: "refund", label: "برگشت وجه" }],
};

export default function PettyCashTab({ employees = [] }) {
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [accOpen, setAccOpen] = useState(false);       // false | "new" | "edit"
  const [reconcile, setReconcile] = useState(null);    // {actual, when, note}
  const [editor, setEditor] = useState({ open: false, index: 0 });
  const [dir, setDir] = useState("out");
  const [postRow, setPostRow] = useState(null);       // {row, type} — نشاندنِ حرکتِ دستی در حسابِ شخص
  const [form] = Form.useForm();
  const [accForm] = Form.useForm();
  const [entryForm] = Form.useForm();

  const loadAccounts = useCallback(() => {
    getPettyAccounts().then((r) => {
      setAccounts(r.data || []);
      setAccId((cur) => cur || (r.data?.[0]?.id ?? null));
      loadPettyAccounts(true);          // کشِ مشترکِ «مبدأ پول» هم تازه شود
    }).catch(() => {});
  }, []);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const load = useCallback(() => {
    if (!accId) { setData(null); return Promise.resolve(); }
    setLoading(true);
    return getPettyEntries({ account_id: accId })
      .then((r) => setData(r.data))
      .catch(() => message.error("خطا در دریافتِ ریزِ صندوق"))
      .finally(() => setLoading(false));
  }, [accId]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); loadAccounts(); };
  const empOpts = useMemo(() => employees.map((e) => ({
    value: e.id, label: `${e.full_name} (${e.employee_code || "—"})` })), [employees]);
  /* v2.170.0 — «حساب دفتر» به‌عنوانِ طرف‌حساب (بی‌ردِ حسابداری)؛ فقط در حرکتِ صندوق، نه نگه‌دارندهٔ صندوق */
  const partyOpts = useMemo(() => [{ value: "office", label: "🏢 حساب دفتر (فقط طرف‌حساب)" }, ...empOpts], [empOpts]);
  const partyBody = (v) => (v === "office" ? { employee_id: null, counterparty: "office" }
    : { employee_id: v || null, counterparty: "" });

  const submitEntry = async (v) => {
    try {
      const r = await addPettyEntry({
        account_id: accId, direction: dir, amount: v.amount,
        occurred_at: v.occurred_at || todayISO(),
        description: v.description || null, ...partyBody(v.employee_id),
        post_to_person: (v.employee_id && v.employee_id !== "office" && v.post_to_person) || null,
      });
      message.success(r.data?.message || "ثبت شد");
      setEntryOpen(false); form.resetFields(); refresh();
    } catch (e) { message.error(apiError(e, "ثبت نشد")); }
  };

  const submitAccount = async (v) => {
    try {
      const payload = { ...v, opening_balance: v.opening_balance || 0, opening_date: v.opening_date || null,
        holder_employee_id: v.holder_employee_id || null, notes: v.notes || null };
      if (accOpen === "edit") {
        await updatePettyAccount(accId, { ...payload, is_active: acc?.is_active !== false });
        message.success("صندوق ویرایش شد");
      } else {
        await createPettyAccount(payload);
        message.success("حسابِ تنخواه ساخته شد");
      }
      setAccOpen(false); accForm.resetFields(); refresh();
    } catch (e) { message.error(apiError(e, "ذخیره نشد")); }
  };

  const acc = data?.account;
  const rows = data?.rows || [];

  const openReconcile = (actual) => setReconcile({
    actual: actual == null ? (acc?.balance ?? 0) : actual, when: todayISO(), note: "",
  });
  const doReconcile = async () => {
    try {
      const r = await reconcilePettyAccount(accId, {
        actual_balance: Number(reconcile.actual) || 0, occurred_at: reconcile.when || todayISO(),
        note: reconcile.note || null,
      });
      message.success(r.data?.message || "ثبت شد");
      setReconcile(null); refresh();
    } catch (e) { message.error(apiError(e, "تطبیق انجام نشد")); }
  };

  const doPost = async () => {
    try {
      const r = await postPettyEntryToPerson(postRow.row.id, { transaction_type: postRow.type });
      message.success(r.data?.message || "در حسابِ شخص نشست");
      setPostRow(null); refresh();
    } catch (e) { message.error(apiError(e, "انجام نشد")); }
  };

  const cols = [
    { title: "شماره سند", dataIndex: "doc_no", width: 120,
      render: (v) => (v ? <code style={{ fontSize: 12 }}>{v}</code> : "—") },
    { title: "تاریخ", dataIndex: "date", width: 110, render: (v) => toJalali(v) },
    { title: "نوع", dataIndex: "direction", width: 90,
      render: (v) => (v === "in" ? <Tag color="green">شارژ</Tag> : <Tag color="red">هزینه</Tag>) },
    { title: "منشأ", dataIndex: "kind", width: 150,
      render: (v, r) => (
        <Space size={4}>
          <Tag color={KIND_COLOR[v] || "default"}>{r.kind_label || v}</Tag>
          {r.linked_doc_no && <code style={{ fontSize: 11 }}>{r.linked_doc_no}</code>}
        </Space>
      ) },
    { title: "مبلغ (ریال)", dataIndex: "amount", align: "left", width: 140, render: (v) => fa(v) },
    { title: "موجودی پس از این سند", dataIndex: "running_balance", align: "left", width: 170,
      render: (v) => <b style={{ color: Number(v) < 0 ? "#C0392B" : "#107C41" }}>{fa(v)}</b> },
    { title: "طرف", dataIndex: "employee_name", width: 150, render: (v) => v || "—" },
    { title: "شرح", dataIndex: "description", render: (v) => v || "—", ellipsis: true },
    { title: "", width: 120, gxExport: false,
      render: (_, r) => (
        <Space size={2}>
          {r.kind === "manual" && r.employee_id && !r.counterparty && (
            <Tooltip title="در حسابِ این شخص هم بنشیند (سندِ مالی با مبدأِ تنخواه)">
              <Button size="small" type="text" icon={<UserSwitchOutlined />}
                onClick={() => setPostRow({ row: r, type: PERSON_TYPES[r.direction][0].value })} />
            </Tooltip>
          )}
          <Tooltip title={r.editable === false
            ? "از دفتر مالی آمده؛ همان سند را ویرایش کنید" : "ویرایش (با قبلی/بعدی)"}>
            <Button size="small" type="text" icon={<EditOutlined />} disabled={r.editable === false}
              onClick={() => setEditor({ open: true, index: rows.findIndex((x) => x.id === r.id) })} />
          </Tooltip>
          <Popconfirm title="حذف شود؟ موجودی دوباره حساب می‌شود." okText="بله" cancelText="خیر"
            disabled={r.editable === false}
            onConfirm={() => deletePettyEntry(r.id)
              .then(() => { message.success("حذف شد"); refresh(); })
              .catch((e) => message.error(apiError(e, "حذف نشد")))}>
            <Button size="small" danger type="text" icon={<DeleteOutlined />} disabled={r.editable === false} />
          </Popconfirm>
        </Space>
      ) },
  ];

  const accountMenu = {
    items: [
      { key: "edit", icon: <EditOutlined />, label: "ویرایش صندوق (نام، نگه‌دارنده، ماندهٔ اول دوره)" },
      { key: "reconcile", icon: <SwapOutlined />, label: "تطبیق موجودی با شمارشِ واقعی" },
      { key: "zero", icon: <SwapOutlined />, label: "صفر کردنِ موجودی", danger: true },
    ],
    onClick: ({ key }) => {
      if (!acc) return;
      if (key === "edit") {
        accForm.setFieldsValue({ name: acc.name, holder_employee_id: acc.holder_employee_id,
          opening_balance: acc.opening_balance, opening_date: acc.opening_date, notes: acc.notes });
        setAccOpen("edit");
      } else if (key === "reconcile") openReconcile(null);
      else if (key === "zero") openReconcile(0);
    },
  };

  const diff = reconcile ? (Number(reconcile.actual) || 0) - (acc?.balance ?? 0) : 0;

  return (
    <Card title="تنخواه"
      extra={
        <Space wrap>
          <Select style={{ width: 220 }} value={accId} onChange={setAccId}
            placeholder="انتخابِ صندوق"
            options={accounts.map((a) => ({ value: a.id, label: a.name }))} />
          <Dropdown menu={accountMenu} disabled={!acc} trigger={["click"]}>
            <Button icon={<SettingOutlined />}>صندوق</Button>
          </Dropdown>
          <Button icon={<ReloadOutlined />} onClick={refresh}>بازخوانی</Button>
          <Button onClick={() => { accForm.resetFields(); setAccOpen("new"); }}>صندوقِ جدید</Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!accId}
            onClick={() => { form.setFieldsValue({ occurred_at: todayISO() }); setEntryOpen(true); }}>
            ثبتِ حرکت
          </Button>
        </Space>
      }>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="موجودی = ماندهٔ اول + شارژها − هزینه‌ها"
        description="پرداخت یا دریافتی که در «اسناد مالی»، «مساعده» یا «پرداخت حقوق» با مبدأِ تنخواه ثبت شود، خودش اینجا می‌نشیند (منشأ: «از دفتر مالی») و از همان‌جا ویرایش می‌شود. حرکت‌های دستی را با دکمهٔ ویرایش، نفر‌به‌نفر اصلاح کنید. «تطبیق موجودی» شمارشِ واقعی را می‌گیرد و مغایرت را سند می‌کند." />

      {acc && (
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="ماندهٔ اول دوره" value={fa(acc.opening)} />
            {acc.opening_date && <Text type="secondary" style={{ fontSize: 12 }}>از {toJalali(acc.opening_date)}</Text>}
          </Card></Col>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="جمع شارژ" value={fa(acc.charged)} valueStyle={{ color: "#107C41" }} /></Card></Col>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="جمع هزینه" value={fa(acc.spent)} valueStyle={{ color: "#C0392B" }} /></Card></Col>
          <Col xs={12} md={6}><Card size="small">
            <Statistic title="موجودیِ فعلی (دفتری)" value={fa(acc.balance)}
              valueStyle={{ fontSize: 20, color: acc.balance < 0 ? "#C0392B" : "#1F4E79" }} />
            {acc.holder_name && <Text type="secondary" style={{ fontSize: 12 }}>نگه‌دارنده: {acc.holder_name}</Text>}
          </Card></Col>
        </Row>
      )}

      <GxTable layoutId="petty-cash-entries" dataSource={rows} columns={cols}
        rowKey="id" size="small" loading={loading} gxExportName="تنخواه"
        pagination={{ pageSize: 50 }} scroll={{ x: "max-content" }} />

      {/* ثبتِ حرکتِ دستی */}
      <Modal open={entryOpen} title="ثبتِ حرکتِ صندوق" onCancel={() => setEntryOpen(false)}
        onOk={() => form.submit()} okText="ثبت" cancelText="انصراف" destroyOnHidden>
        <Segmented block value={dir} style={{ marginBottom: 12 }}
          onChange={(v) => { setDir(v); form.setFieldValue("post_to_person", undefined); }}
          options={[{ value: "out", label: "هزینه (از صندوق)" }, { value: "in", label: "شارژ (به صندوق)" }]} />
        <Form form={form} layout="vertical" onFinish={submitEntry} style={{ direction: "rtl" }}>
          <Form.Item name="amount" label="مبلغ (ریال)" rules={[{ required: true }]}>
            <MoneyInput words previewLabel="مبلغ" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="occurred_at" label="تاریخ"><JalaliDateInput style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="employee_id" label="طرفِ حساب (اختیاری)">
            <Select showSearch allowClear placeholder="اگر مربوط به یک نفر یا حساب دفتر است"
              filterOption={filterByChildren} options={partyOpts} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.employee_id !== b.employee_id}>
            {({ getFieldValue }) => getFieldValue("employee_id") && getFieldValue("employee_id") !== "office" ? (
              <Form.Item name="post_to_person" label="در حسابِ این شخص هم بنشیند؟"
                extra="مثلاً مساعدهٔ نقدی به کسی که شماره کارت ندارد: مبلغ در مانده/فیشِ همان نفر می‌آید و حرکتِ صندوق آینهٔ آن سند می‌شود.">
                <Select allowClear placeholder="خیر — فقط هزینهٔ صندوق"
                  options={PERSON_TYPES[dir]} />
              </Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item name="description" label="شرح"><Input /></Form.Item>
        </Form>
      </Modal>

      {/* نشاندنِ حرکتِ دستیِ موجود در حسابِ شخص */}
      <Modal open={!!postRow} title="ثبت در حسابِ شخص" onCancel={() => setPostRow(null)}
        onOk={doPost} okText="ثبت در حساب" cancelText="انصراف" destroyOnHidden>
        {postRow && (
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            <Text>
              <code>{postRow.row.doc_no}</code> • {toJalali(postRow.row.date)} • <b>{fa(postRow.row.amount)}</b> ریال
              • <b>{postRow.row.employee_name}</b>
            </Text>
            <Select style={{ width: "100%" }} value={postRow.type}
              onChange={(v) => setPostRow((p) => ({ ...p, type: v }))}
              options={PERSON_TYPES[postRow.row.direction]} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              یک سندِ مالی با مبدأِ همین صندوق برای این شخص ساخته می‌شود و این حرکت به آن وصل می‌شود
              (موجودیِ صندوق عوض نمی‌شود). از این به بعد ویرایش/حذف از «اسناد مالی» است.
            </Text>
          </Space>
        )}
      </Modal>

      {/* ویرایشِ نفر‌به‌نفرِ حرکت‌ها — همان ویرایشگرِ رکوردِ حقوق */}
      <RecordEditorModal
        open={editor.open} onClose={() => setEditor({ open: false, index: 0 })}
        items={rows} initialIndex={editor.index} form={entryForm}
        title="ویرایش حرکت صندوق" width={640}
        headerTitle={(r) => `${r.doc_no || "—"} • ${r.direction === "in" ? "شارژ" : "هزینه"}`}
        headerSubtitle={(r) => `${toJalali(r.date)} • ${fa(r.amount)} ریال${r.employee_name ? ` • ${r.employee_name}` : ""}`}
        headerTag={(r) => <Tag color={KIND_COLOR[r.kind] || "default"}>{r.kind_label}</Tag>}
        loadValues={(r) => ({ direction: r.direction, amount: Number(r.amount) || null,
          occurred_at: r.date, employee_id: r.counterparty === "office" ? "office" : (r.employee_id ?? null),
          description: r.description || "" })}
        renderFields={(r) => (
          <>
            {r.editable === false && (
              <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                message={`این حرکت از سندِ مالیِ ${r.linked_doc_no || ""} آمده و اینجا فقط‌خواندنی است؛ برای تغییر، همان سند را در «اسناد مالی» ویرایش کنید.`} />
            )}
            <Form.Item name="direction" label="نوع">
              <Segmented disabled={r.editable === false}
                options={[{ value: "out", label: "هزینه (از صندوق)" }, { value: "in", label: "شارژ (به صندوق)" }]} />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} sm={12}><Form.Item name="amount" label="مبلغ (ریال)" rules={[{ required: true }]}>
                <MoneyInput disabled={r.editable === false} /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="occurred_at" label="تاریخ" rules={[{ required: true }]}>
                <JalaliDateInput style={{ width: "100%" }} disabled={r.editable === false} /></Form.Item></Col>
            </Row>
            <Form.Item name="employee_id" label="طرفِ حساب">
              <Select showSearch allowClear disabled={r.editable === false}
                filterOption={filterByChildren} options={partyOpts} />
            </Form.Item>
            <Form.Item name="description" label="شرح"><Input disabled={r.editable === false} /></Form.Item>
            <Text type="secondary" style={{ fontSize: 12 }}>شمارهٔ سند با ویرایش عوض نمی‌شود.</Text>
          </>
        )}
        onSave={async (item, changed, all) => {
          if (item.editable === false) { message.warning("این حرکت از دفتر مالی آمده"); return false; }
          const body = { ...changed };
          if ("employee_id" in changed) Object.assign(body, partyBody(changed.employee_id));
          if (all.employee_id == null && (item.employee_id != null || item.counterparty)) {
            body.clear_employee = true; body.counterparty = "";
          }
          try {
            await updatePettyEntry(item.id, body);
            message.success("ویرایش شد");
            await load(); loadAccounts();
            return true;
          } catch (e) { message.error(apiError(e, "ذخیره نشد")); return false; }
        }} />

      {/* تطبیقِ موجودی */}
      <Modal open={!!reconcile} title="تطبیق موجودی صندوق" onCancel={() => setReconcile(null)}
        onOk={doReconcile} okText="ثبت سند تطبیق" cancelText="انصراف" destroyOnHidden>
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          <Text>موجودیِ دفتری الان: <b>{fa(acc?.balance)}</b> ریال</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>موجودیِ واقعی (شمارش‌شده)</Text>
            <MoneyInput value={reconcile?.actual} style={{ width: "100%" }}
              onChange={(v) => setReconcile((p) => ({ ...p, actual: v }))} />
          </div>
          <Text style={{ color: diff === 0 ? undefined : diff > 0 ? "#107C41" : "#C0392B" }}>
            {diff === 0 ? "مغایرتی نیست"
              : `مغایرت: ${diff > 0 ? "+" : "−"}${fa(Math.abs(diff))} ریال — یک سندِ ${diff > 0 ? "شارژ" : "هزینه"}ِ تطبیق ثبت می‌شود`}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>تاریخ</Text>
            <JalaliDateInput style={{ width: "100%" }} value={reconcile?.when}
              onChange={(v) => setReconcile((p) => ({ ...p, when: v }))} />
          </div>
          <Input placeholder="توضیح (اختیاری) — مثلاً «مغایرتِ شمارشِ پایانِ شهریور»" value={reconcile?.note}
            onChange={(e) => setReconcile((p) => ({ ...p, note: e.target.value }))} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            سابقه پاک نمی‌شود؛ فقط یک سندِ «تطبیق موجودی» اضافه می‌شود تا موجودی همان عددِ واقعی شود.
          </Text>
        </Space>
      </Modal>

      {/* ساخت/ویرایشِ صندوق */}
      <Modal open={!!accOpen} title={accOpen === "edit" ? "ویرایش صندوق" : "حسابِ تنخواهِ جدید"}
        onCancel={() => setAccOpen(false)} onOk={() => accForm.submit()}
        okText={accOpen === "edit" ? "ذخیره" : "ساختن"} cancelText="انصراف" destroyOnHidden>
        <Form form={accForm} layout="vertical" onFinish={submitAccount} style={{ direction: "rtl" }}>
          <Form.Item name="name" label="نامِ صندوق" rules={[{ required: true }]}>
            <Input placeholder="مثلاً تنخواهِ کارگاه" />
          </Form.Item>
          <Form.Item name="holder_employee_id" label="نگه‌دارنده (اختیاری)">
            <Select showSearch allowClear filterOption={filterByChildren} options={empOpts} />
          </Form.Item>
          <Form.Item name="opening_balance" label="ماندهٔ اول دوره (ریال)"
            extra="نقطهٔ شروعِ حسابِ صندوق؛ همهٔ حرکت‌ها روی همین سوار می‌شوند.">
            <MoneyInput style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="opening_date" label="تاریخِ ماندهٔ اول"><JalaliDateInput style={{ width: "100%" }} /></Form.Item>
          <Form.Item name="notes" label="توضیح"><Input /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
